import { useDeferredValue, useEffect, useRef, useState, type JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isHTTPError } from 'ky';
import { ChevronDown, ChevronUp, Heart, LayoutGrid, Pencil, X } from 'lucide-react';
import { CreateTrialBarRequestSchema, type TrialBarDto, type TrialListQuery } from '@ctms/contracts';

import { getApiErrorMessage } from '../../../api/client.js';
import { trialsApi } from '../../trials/trials.api.js';
import { trialQueryKeys } from '../../trials/trials.query-keys.js';
import { cockpitApi } from '../cockpit.api.js';
import { cockpitQueryKeys } from '../cockpit.query-keys.js';
import { useTrialBars } from '../trial-bars/use-trial-bars-query.js';

function queryRetry(count: number, err: unknown): boolean {
  return isHTTPError(err) ? false : count < 3;
}

/** Piotr #7 odp.5 — the shortcut WRITE limit (the read DTO stays lenient at 20). */
const SHORTCUT_MAX = 10;
const SHORTCUT_MAX_MESSAGE = 'Skrót może mieć maksymalnie 10 znaków';

export interface BarManagerDialogProps {
  isOpen: boolean;
  /** RBAC gate (trials:write) — without it the shortcut edit is unavailable (D2). */
  canWriteTrials: boolean;
  onClose: () => void;
}

/** Capacity field: '' = no bound (null); anything else must be a positive integer. */
function parseCapacity(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return null;
  }
  const value = Number(trimmed);
  if (!Number.isInteger(value) || value <= 0) {
    return undefined;
  }
  return value;
}

/**
 * The grid (#7 A5, frozen feature trial-bars-and-search.feature): the bar
 * definition window, PRIVATE to the logged-in user (the server scopes every
 * call by session) and WITHOUT the legacy DEFAULT button (removed from scope,
 * Piotr 01.11).
 *
 * Layout mirrors wireframe A3 (spec/issues/7-wireframes-bars.md): left column
 * manages bars (pick, create with a typed capacity, rename/re-capacity,
 * delete, reorder), right column manages the selected bar's items (order,
 * remove, heart-toggle add with server search) plus the live-preview strip at
 * cockpit target width — the overflow guardian of decision D1 (horizontal
 * scrolling on the main screen stays an emergency fallback only).
 *
 * Persistence is immediate (each mutation invalidates `cockpitQueryKeys.bars()`
 * right away), matching the create-trial-dialog pattern — no form-level save.
 *
 * Shortcut editing is a GLOBAL change (REQ-TRIAL-001 — every user of the site
 * sees the new shortcut), so it rides `PATCH /trials/:id` behind
 * `trials:write`; without the permission the field renders disabled with an
 * explanatory hint (decision D2, frozen scenario). Client-side the ≤10-char
 * limit (Piotr odp.5) is enforced before the request is sent.
 */
export function BarManagerDialog({
  isOpen,
  canWriteTrials,
  onClose,
}: BarManagerDialogProps): JSX.Element | null {
  const queryClient = useQueryClient();
  const barsQuery = useTrialBars(isOpen);

  const [selectedBarId, setSelectedBarId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // New-bar form (wireframe: [Nowy pasek]).
  const [newBarOpen, setNewBarOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCapacity, setNewCapacity] = useState('');

  // Rename/re-capacity form (wireframe: [Zmień nazwę]).
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [renameCapacity, setRenameCapacity] = useState('');

  // Inline shortcut edit of one item (global change, trials:write only).
  const [shortcutEdit, setShortcutEdit] = useState<{ trialId: number; value: string } | null>(
    null,
  );
  const [shortcutError, setShortcutError] = useState<string | null>(null);

  // Add-trial search panel (frozen: typing "GASTRO" narrows the pick list).
  const [addPhrase, setAddPhrase] = useState('');
  const deferredAddPhrase = useDeferredValue(addPhrase);

  // Live-preview overflow (D1): measured on the preview strip, blocks adds.
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [overflowing, setOverflowing] = useState(false);

  const bars: TrialBarDto[] = barsQuery.data?.bars ?? [];
  const selectedBar = bars.find((bar) => bar.id === selectedBarId) ?? bars[0] ?? null;
  const isFull =
    selectedBar !== null &&
    selectedBar.capacity !== null &&
    selectedBar.items.length >= selectedBar.capacity;

  const addQuery: TrialListQuery = {
    page: 1,
    limit: 100,
    active: true,
    ...(deferredAddPhrase.trim() !== '' ? { search: deferredAddPhrase.trim() } : {}),
  };
  const addTrialsQuery = useQuery({
    queryKey: trialQueryKeys.list(addQuery),
    queryFn: () => trialsApi.listTrials(addQuery),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });

  // Re-measure the preview whenever the selected bar's buttons change. In
  // jsdom scrollWidth/clientWidth are 0, so tests never see an overflow —
  // the guard is exercised in E2E (A8) at the real cockpit width.
  const previewSignature = selectedBar
    ? `${selectedBar.id}:${selectedBar.items.map((item) => `${item.id}/${item.shortcut}`).join(',')}`
    : '';
  useEffect(() => {
    const el = previewRef.current;
    if (!isOpen || el === null) {
      setOverflowing(false);
      return;
    }
    setOverflowing(el.scrollWidth > el.clientWidth);
  }, [isOpen, previewSignature]);

  if (!isOpen) {
    return null;
  }

  /** One mutation + bars invalidation; a failure surfaces in the error slot. */
  async function run<T>(action: () => Promise<T>): Promise<T | null> {
    setError(null);
    try {
      const result = await action();
      await queryClient.invalidateQueries({ queryKey: cockpitQueryKeys.bars() });
      return result;
    } catch (err) {
      setError(await getApiErrorMessage(err));
      return null;
    }
  }

  const handleCreateBar = async (): Promise<void> => {
    setFormError(null);
    const name = newName.trim();
    if (name === '') {
      setFormError('Nazwa paska jest wymagana');
      return;
    }
    const capacity = parseCapacity(newCapacity);
    if (capacity === undefined) {
      setFormError('Pojemność musi być liczbą całkowitą większą od zera');
      return;
    }
    const parsed = CreateTrialBarRequestSchema.safeParse({ name, capacity });
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? 'Nieprawidłowe dane paska');
      return;
    }
    const created = await run(() => cockpitApi.createBar(parsed.data));
    if (created !== null) {
      setSelectedBarId(created.id);
      setNewBarOpen(false);
      setNewName('');
      setNewCapacity('');
    }
  };

  const handleSaveBar = async (): Promise<void> => {
    if (selectedBar === null) {
      return;
    }
    setFormError(null);
    const name = renameName.trim();
    if (name === '') {
      setFormError('Nazwa paska jest wymagana');
      return;
    }
    const capacity = parseCapacity(renameCapacity);
    if (capacity === undefined) {
      setFormError('Pojemność musi być liczbą całkowitą większą od zera');
      return;
    }
    const saved = await run(() => cockpitApi.updateBar(selectedBar.id, { name, capacity }));
    if (saved !== null) {
      setRenameOpen(false);
    }
  };

  const handleDeleteBar = async (): Promise<void> => {
    if (selectedBar === null) {
      return;
    }
    const deleted = await run(() => cockpitApi.deleteBar(selectedBar.id));
    if (deleted !== null) {
      setSelectedBarId(null);
      setRenameOpen(false);
    }
  };

  const moveBar = (direction: -1 | 1): void => {
    if (selectedBar === null) {
      return;
    }
    const ids = bars.map((bar) => bar.id);
    const from = ids.indexOf(selectedBar.id);
    const to = from + direction;
    const fromId = ids[from];
    const toId = ids[to];
    if (fromId === undefined || toId === undefined) {
      return;
    }
    ids[from] = toId;
    ids[to] = fromId;
    void run(() => cockpitApi.reorderBars({ ids }));
  };

  const moveItem = (itemId: number, direction: -1 | 1): void => {
    if (selectedBar === null) {
      return;
    }
    const ids = selectedBar.items.map((item) => item.id);
    const from = ids.indexOf(itemId);
    const to = from + direction;
    const fromId = ids[from];
    const toId = ids[to];
    if (fromId === undefined || toId === undefined) {
      return;
    }
    ids[from] = toId;
    ids[to] = fromId;
    void run(() => cockpitApi.reorderItems(selectedBar.id, { ids }));
  };

  const handleHeart = (trialId: number): void => {
    if (selectedBar === null) {
      return;
    }
    const existing = selectedBar.items.find((item) => item.trialId === trialId);
    if (existing !== undefined) {
      void run(() => cockpitApi.removeItem(selectedBar.id, existing.id));
      return;
    }
    // Frozen: "próba dodania siódmej pozycji zostaje zablokowana" — the
    // capacity bound and the preview overflow block BEFORE any API call.
    if (isFull || overflowing) {
      return;
    }
    void run(() => cockpitApi.addItem(selectedBar.id, { trialId }));
  };

  const handleSaveShortcut = async (): Promise<void> => {
    if (shortcutEdit === null) {
      return;
    }
    const value = shortcutEdit.value.trim();
    if (value === '') {
      setShortcutError('Skrót jest wymagany');
      return;
    }
    if (value.length > SHORTCUT_MAX) {
      setShortcutError(SHORTCUT_MAX_MESSAGE);
      return;
    }
    setShortcutError(null);
    setError(null);
    try {
      await trialsApi.updateTrial(shortcutEdit.trialId, { shortcut: value });
      await queryClient.invalidateQueries({ queryKey: cockpitQueryKeys.bars() });
      await queryClient.invalidateQueries({ queryKey: trialQueryKeys.all });
      setShortcutEdit(null);
    } catch (err) {
      setShortcutError(await getApiErrorMessage(err));
    }
  };

  const openRename = (): void => {
    if (selectedBar === null) {
      return;
    }
    setFormError(null);
    setRenameName(selectedBar.name);
    setRenameCapacity(selectedBar.capacity === null ? '' : String(selectedBar.capacity));
    setRenameOpen(true);
  };

  // Alphabetical by shortcut, the same stance as the loupe (A6).
  const addResults = [...(addTrialsQuery.data?.items ?? [])].sort((a, b) =>
    a.shortcut.localeCompare(b.shortcut),
  );

  const inputClass =
    'w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none';
  const buttonClass =
    'rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-300 hover:border-cyan-700 hover:text-cyan-200 disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-xs">
      <div
        role="dialog"
        aria-labelledby="bar-manager-title"
        data-testid="bar-manager-dialog"
        className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-y-auto rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
          <h2 id="bar-manager-title" className="flex items-center gap-2 text-base font-semibold">
            <LayoutGrid className="h-4 w-4 text-cyan-400" />
            Moje ulubione paski badań
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zamknij"
            className="rounded-md p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mt-2 text-xs text-slate-400">
          Każdy użytkownik ma własne paski — są prywatne i przechowywane w bazie systemu.
        </p>

        {error !== null ? (
          <p data-testid="bar-manager-error" className="mt-3 text-sm text-rose-400">
            {error}
          </p>
        ) : null}
        {formError !== null ? (
          <p data-testid="bar-manager-validation" className="mt-3 text-sm text-rose-400">
            {formError}
          </p>
        ) : null}

        <div className="mt-4 grid min-h-0 flex-1 grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
          {/* Left column — bar management */}
          <div className="flex flex-col gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pasek</h3>
            {bars.length === 0 ? (
              <p data-testid="bar-manager-empty" className="text-sm text-slate-400">
                {barsQuery.isError
                  ? 'Nie udało się wczytać pasków. Spróbuj ponownie.'
                  : 'Nie masz jeszcze żadnych pasków — utwórz pierwszy przyciskiem „Nowy pasek".'}
              </p>
            ) : (
              <>
                <select
                  data-testid="bar-manager-bar-select"
                  aria-label="Wybierz pasek do edycji"
                  value={String(selectedBar?.id ?? '')}
                  onChange={(event) => {
                    setSelectedBarId(Number(event.target.value));
                    setRenameOpen(false);
                  }}
                  className="w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100 scheme-dark"
                >
                  {bars.map((bar) => (
                    <option key={bar.id} value={String(bar.id)}>
                      {bar.name}
                      {bar.capacity === null ? '' : ` (poj. ${bar.capacity})`}
                    </option>
                  ))}
                </select>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    data-testid="bar-manager-bar-up"
                    onClick={() => moveBar(-1)}
                    disabled={bars.indexOf(selectedBar as TrialBarDto) <= 0}
                    aria-label="Przesuń pasek w górę"
                    className={buttonClass}
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    data-testid="bar-manager-bar-down"
                    onClick={() => moveBar(1)}
                    disabled={bars.indexOf(selectedBar as TrialBarDto) >= bars.length - 1}
                    aria-label="Przesuń pasek w dół"
                    className={buttonClass}
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    data-testid="bar-manager-rename-bar"
                    onClick={openRename}
                    className={buttonClass}
                  >
                    Zmień nazwę
                  </button>
                  <button
                    type="button"
                    data-testid="bar-manager-delete-bar"
                    onClick={() => void handleDeleteBar()}
                    className={`${buttonClass} hover:border-rose-700 hover:text-rose-300`}
                  >
                    Usuń pasek
                  </button>
                </div>

                {renameOpen && selectedBar !== null ? (
                  <div className="flex flex-col gap-2 rounded-md border border-slate-800 bg-slate-950/60 p-3">
                    <label className="text-xs text-slate-400" htmlFor="bar-rename-name">
                      Nazwa paska
                    </label>
                    <input
                      id="bar-rename-name"
                      type="text"
                      data-testid="bar-manager-rename-name"
                      value={renameName}
                      onChange={(event) => setRenameName(event.target.value)}
                      className={inputClass}
                    />
                    <label className="text-xs text-slate-400" htmlFor="bar-rename-capacity">
                      Pojemność (liczba pozycji, puste = brak limitu)
                    </label>
                    <input
                      id="bar-rename-capacity"
                      type="text"
                      inputMode="numeric"
                      data-testid="bar-manager-rename-capacity"
                      value={renameCapacity}
                      onChange={(event) => setRenameCapacity(event.target.value)}
                      className={inputClass}
                    />
                    <button
                      type="button"
                      data-testid="bar-manager-save-bar"
                      onClick={() => void handleSaveBar()}
                      className={buttonClass}
                    >
                      Zapisz pasek
                    </button>
                  </div>
                ) : null}
              </>
            )}

            <button
              type="button"
              data-testid="bar-manager-new-bar"
              onClick={() => {
                setFormError(null);
                setNewBarOpen((open) => !open);
              }}
              className={`${buttonClass} self-start`}
            >
              Nowy pasek
            </button>
            {newBarOpen ? (
              <div className="flex flex-col gap-2 rounded-md border border-slate-800 bg-slate-950/60 p-3">
                <label className="text-xs text-slate-400" htmlFor="bar-new-name">
                  Nazwa paska
                </label>
                <input
                  id="bar-new-name"
                  type="text"
                  data-testid="bar-manager-new-name"
                  value={newName}
                  onChange={(event) => setNewName(event.target.value)}
                  placeholder="np. PORANNY"
                  className={inputClass}
                />
                <label className="text-xs text-slate-400" htmlFor="bar-new-capacity">
                  Pojemność (liczba pozycji, puste = brak limitu)
                </label>
                <input
                  id="bar-new-capacity"
                  type="text"
                  inputMode="numeric"
                  data-testid="bar-manager-new-capacity"
                  value={newCapacity}
                  onChange={(event) => setNewCapacity(event.target.value)}
                  placeholder="np. 6"
                  className={inputClass}
                />
                <button
                  type="button"
                  data-testid="bar-manager-create-bar"
                  onClick={() => void handleCreateBar()}
                  className={buttonClass}
                >
                  Utwórz pasek
                </button>
              </div>
            ) : null}
          </div>

          {/* Right column — items of the selected bar + add panel */}
          <div className="flex min-w-0 flex-col gap-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Badania na tym pasku (kolejność na liście = kolejność przycisków)
            </h3>
            {selectedBar === null ? (
              <p className="text-sm text-slate-400">Wybierz albo utwórz pasek, aby dodać badania.</p>
            ) : (
              <>
                <ul className="flex flex-col gap-1.5" data-testid="bar-manager-items">
                  {selectedBar.items.map((item, index) => (
                    <li
                      key={item.id}
                      data-testid={`bar-manager-item-${item.id}`}
                      className="flex flex-wrap items-center gap-2 rounded-md border border-slate-800 bg-slate-950/40 px-3 py-2"
                    >
                      <span className="w-5 text-xs text-slate-500">{index + 1}.</span>
                      <span className="min-w-0 flex-1 truncate text-sm">
                        {item.shortTitle}{' '}
                        <span className="text-xs text-slate-400">ośrodek {item.siteNumber}</span>
                      </span>
                      {shortcutEdit !== null && shortcutEdit.trialId === item.trialId ? (
                        <span className="flex items-center gap-1">
                          <input
                            type="text"
                            data-testid={`bar-manager-shortcut-input-${item.trialId}`}
                            value={shortcutEdit.value}
                            onChange={(event) =>
                              setShortcutEdit({ trialId: item.trialId, value: event.target.value })
                            }
                            className="w-32 rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100 focus:border-cyan-500 focus:outline-none"
                          />
                          <button
                            type="button"
                            data-testid={`bar-manager-save-shortcut-${item.trialId}`}
                            onClick={() => void handleSaveShortcut()}
                            className={buttonClass}
                          >
                            Zapisz
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShortcutEdit(null);
                              setShortcutError(null);
                            }}
                            aria-label="Anuluj edycję skrótu"
                            className={buttonClass}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      ) : canWriteTrials ? (
                        <span className="flex items-center gap-1">
                          <span
                            data-testid={`bar-manager-shortcut-${item.trialId}`}
                            className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-xs font-bold text-cyan-300"
                          >
                            {item.shortcut}
                          </span>
                          <button
                            type="button"
                            data-testid={`bar-manager-edit-shortcut-${item.trialId}`}
                            onClick={() => {
                              setShortcutError(null);
                              setShortcutEdit({ trialId: item.trialId, value: item.shortcut });
                            }}
                            aria-label={`Edytuj skrót badania ${item.shortcut}`}
                            title="Zmień skrót (globalnie dla całego ośrodka)"
                            className={buttonClass}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <input
                            type="text"
                            value={item.shortcut}
                            disabled
                            aria-label={`Skrót badania ${item.shortcut} (tylko do odczytu)`}
                            className="w-32 cursor-not-allowed rounded-md border border-slate-800 bg-slate-950 px-2 py-1 text-xs text-slate-500"
                          />
                          <span className="text-[10px] text-slate-500">
                            Zmiana skrótu wymaga uprawnienia do edycji badań (trials:write)
                          </span>
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <button
                          type="button"
                          data-testid={`bar-manager-item-up-${item.id}`}
                          onClick={() => moveItem(item.id, -1)}
                          disabled={index === 0}
                          aria-label="Przesuń pozycję w górę"
                          className={buttonClass}
                        >
                          <ChevronUp className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          data-testid={`bar-manager-item-down-${item.id}`}
                          onClick={() => moveItem(item.id, 1)}
                          disabled={index === selectedBar.items.length - 1}
                          aria-label="Przesuń pozycję w dół"
                          className={buttonClass}
                        >
                          <ChevronDown className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          data-testid={`bar-manager-remove-item-${item.id}`}
                          onClick={() => void run(() => cockpitApi.removeItem(selectedBar.id, item.id))}
                          aria-label={`Usuń badanie ${item.shortcut} z paska`}
                          className={`${buttonClass} hover:border-rose-700 hover:text-rose-300`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    </li>
                  ))}
                  {selectedBar.items.length === 0 ? (
                    <li className="text-sm text-slate-400">
                      Pasek jest pusty — dodaj badania serduszkiem poniżej.
                    </li>
                  ) : null}
                </ul>
                {shortcutError !== null ? (
                  <p data-testid="bar-manager-shortcut-error" className="text-sm text-rose-400">
                    {shortcutError}
                  </p>
                ) : null}

                {/* Add-trial search panel */}
                <div className="rounded-md border border-slate-800 bg-slate-950/40 p-3">
                  <h4 className="text-xs font-semibold text-slate-300">Dodaj badania do paska</h4>
                  <input
                    type="text"
                    data-testid="bar-manager-add-search"
                    value={addPhrase}
                    onChange={(event) => setAddPhrase(event.target.value)}
                    placeholder="Wpisz skrót, nazwę lub numer ośrodka, np. GASTRO"
                    aria-label="Szukaj badania do dodania"
                    className={`${inputClass} mt-2`}
                  />
                  {isFull && selectedBar.capacity !== null ? (
                    <p data-testid="bar-manager-capacity-full" className="mt-2 text-xs text-amber-400">
                      Pasek jest pełny ({selectedBar.items.length}/{selectedBar.capacity}) — aby
                      dodać nową pozycję, najpierw usuń istniejącą.
                    </p>
                  ) : null}
                  {overflowing ? (
                    <p data-testid="bar-manager-overflow" className="mt-2 text-xs text-amber-400">
                      Kolejna pozycja nie zmieści się w docelowej szerokości kokpitu — dodawanie
                      jest zablokowane.
                    </p>
                  ) : null}
                  {addTrialsQuery.isError ? (
                    <p className="mt-2 text-sm text-rose-400">
                      Nie udało się wczytać listy badań. Spróbuj ponownie.
                    </p>
                  ) : (
                    <ul
                      data-testid="bar-manager-add-results"
                      className="mt-2 max-h-48 overflow-y-auto"
                    >
                      {addResults.map((trial) => {
                        const onBar = selectedBar.items.some(
                          (item) => item.trialId === trial.id,
                        );
                        return (
                          <li key={trial.id}>
                            <button
                              type="button"
                              data-testid={`bar-manager-heart-${trial.id}`}
                              onClick={() => handleHeart(trial.id)}
                              aria-pressed={onBar}
                              title={
                                onBar
                                  ? 'Serduszko wypełnione — kliknij, aby usunąć z paska'
                                  : 'Kliknij serduszko, aby dodać do paska'
                              }
                              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-slate-800"
                            >
                              <Heart
                                className={
                                  onBar
                                    ? 'h-4 w-4 fill-rose-500 text-rose-500'
                                    : 'h-4 w-4 text-slate-500'
                                }
                              />
                              <span className="text-sm font-bold text-cyan-300">
                                {trial.shortcut}
                              </span>
                              <span className="truncate text-sm text-slate-100">
                                {trial.shortTitle}
                              </span>
                              <span className="ml-auto text-xs text-slate-400">
                                ośrodek {trial.siteNumber}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {!addTrialsQuery.isError &&
                  !addTrialsQuery.isLoading &&
                  addResults.length === 0 ? (
                    <p className="mt-2 text-sm text-slate-400">
                      Brak badań spełniających kryteria.
                    </p>
                  ) : null}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Live-preview strip at cockpit target width (D1) */}
        <div className="mt-4 border-t border-slate-800 pt-3">
          <p className="text-[11px] uppercase tracking-wide text-slate-500">
            Podgląd na żywo — tak pasek będzie wyglądał w kokpicie:
          </p>
          <div
            ref={previewRef}
            data-testid="bar-manager-preview"
            className="mt-2 flex min-w-0 items-center gap-1 overflow-hidden rounded-md border border-slate-800 bg-slate-950/60 px-2 py-1"
          >
            {selectedBar !== null ? (
              <>
                <span className="shrink-0 text-xs font-bold tracking-wide text-cyan-300">
                  {selectedBar.name}
                </span>
                {selectedBar.items.map((item) => (
                  <span
                    key={item.id}
                    className="shrink-0 rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-xs font-medium text-slate-200"
                  >
                    {item.shortcut}
                  </span>
                ))}
              </>
            ) : null}
          </div>
          {selectedBar !== null && selectedBar.capacity !== null ? (
            <p data-testid="bar-manager-preview-count" className="mt-1 text-xs text-slate-400">
              {selectedBar.items.length}/{selectedBar.capacity}
              {isFull ? ' — pasek pełny' : ''}
            </p>
          ) : null}
          <p data-testid="bar-manager-instruction" className="mt-2 text-[11px] text-slate-500">
            Zaleca się krótkie, charakterystyczne nazwy. Skrót badania (max {SHORTCUT_MAX} znaków)
            możesz zmienić tutaj — pamiętaj, że zmiana dotyczy całego ośrodka, nie tylko Twojego
            paska.
          </p>
        </div>

        <div className="mt-4 flex justify-end border-t border-slate-800 pt-4">
          <button
            type="button"
            data-testid="bar-manager-close"
            onClick={onClose}
            className="rounded-md bg-cyan-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-cyan-500"
          >
            Zamknij
          </button>
        </div>
      </div>
    </div>
  );
}
